import { Injectable, Inject } from "@angular/core";
import { LOCATION, LOCATION_SERVICE } from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";

/**
 * Serviço responsável por centralizar operações relacionadas à hierarquia de localização de dispositivos
 */
@Injectable({
    providedIn: "root"
})
export class LocationDeviceService {
    private locationsById: any = {};
    private readonly EMPTY_LOCATION_LABEL = "";

    constructor(@Inject(LOCATION) private readonly location: any,
                @Inject(LOCATION_SERVICE) private readonly locationService: any) {}

    /**
     * Carrega todas as localidades e indexa por ID
     */
    async loadAndIndexLocations(): Promise<void> {
        const locations: any[] = await this.locationService.getAllLocations();
        this.locationsById = {};
        locations.forEach(location => {
            this.locationsById[location.locationId] = location;
        });
    }

    /**
     * Retorna um array de 'path' de locations, ordenado de acordo com a hierarquia.
     * Em casos onde não for encontrado location pelo id dado (por motivo de inconsistência da base),
     * será adicionado 'null' na posição correspondente do array para representar a sua ausência.
     */
    getLocationHierarchyPaths(locationId: number, allLocations?: any, processedLocations: string[] = [],
        visitedIds: Set<number> = new Set()): string[] {

        const locationsMap = allLocations || this.locationsById;

        if (visitedIds.has(locationId)) {
            return processedLocations;
        }

        const location = locationsMap[locationId];
        if (location) {
            visitedIds.add(locationId);

            if (location.locationParentId !== locationId &&
                location.locationParentId !== this.location.DEFAULT_LOCATION_ID &&
                !visitedIds.has(location.locationParentId)) {
                this.getLocationHierarchyPaths(location.locationParentId, locationsMap, processedLocations, visitedIds);
            }

            processedLocations.push(location.locationPath);
        } else {
            processedLocations.push(null);
        }

        return processedLocations;
    }

    /**
     * Retorna o label para representação da location, incluindo toda a hierarquia dividida por '/'.
     * Caso exista alguma location inválida em qualquer nível da hierarquia será retornado um label vazio.
     */
    getLocationLabel(locationId: number, allLocations?: any): string {
        if (locationId === 0 || locationId === this.location.DEFAULT_LOCATION_ID) {
            return this.EMPTY_LOCATION_LABEL;
        }

        const locationHierarchy = this.getLocationHierarchyPaths(locationId, allLocations);

        if (locationHierarchy.some(path => path === null)) {
            return this.EMPTY_LOCATION_LABEL;
        }

        const validPaths = locationHierarchy.filter(path =>
            path !== null && path.toLowerCase() !== "root"
        );

        if (validPaths.length === 0) {
            return this.EMPTY_LOCATION_LABEL;
        } else if (validPaths.length === 1) {
            return `/${validPaths[0]}`;
        } else {
            return `/${validPaths.join("/")}`;
        }
    }
}
